import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Upload, FileUp, CheckCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export const CSVUpload = ({ onUploadComplete }) => {
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.name.endsWith('.csv') && !file.name.endsWith('.txt')) {
      toast.error("Please upload a .csv or .txt file");
      return;
    }

    setUploading(true);
    setResult(null);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await api.post("/domains/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(res.data);
      toast.success(res.data.message);
      onUploadComplete?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFile(file);
  };

  const reset = () => {
    setResult(null);
    setUploading(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button
          data-testid="upload-csv-btn"
          className="h-9 bg-blue-600 hover:bg-blue-500 text-white text-sm rounded-sm"
        >
          <Upload className="w-4 h-4 mr-2" /> Upload CSV
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md" data-testid="upload-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold text-white">Import Domains</DialogTitle>
          <DialogDescription className="text-sm text-zinc-500">Upload a CSV file with domain names to import</DialogDescription>
        </DialogHeader>

        {!result ? (
          <div
            className={`upload-zone rounded-sm p-8 text-center ${dragOver ? 'dragover' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
          >
            {uploading ? (
              <div className="space-y-3">
                <Loader2 className="w-8 h-8 text-blue-400 mx-auto animate-spin" />
                <p className="text-sm text-zinc-300">Importing domains...</p>
                <p className="text-xs text-zinc-500">Processing in 10k batches</p>
              </div>
            ) : (
              <div className="space-y-3">
                <FileUp className="w-8 h-8 text-zinc-500 mx-auto" />
                <div>
                  <p className="text-sm text-zinc-300 mb-1">Drop CSV file here or click to browse</p>
                  <p className="text-xs text-zinc-600">Single column CSV with domain names (e.g., example.de)</p>
                </div>
                <Button
                  data-testid="browse-file-btn"
                  variant="outline"
                  size="sm"
                  onClick={() => fileRef.current?.click()}
                  className="border-zinc-700 text-zinc-300 rounded-sm"
                >
                  Browse Files
                </Button>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".csv,.txt"
                  className="hidden"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                  data-testid="file-input"
                />
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-4 space-y-4" data-testid="upload-result">
            <CheckCircle className="w-10 h-10 text-emerald-400 mx-auto" />
            <div>
              <p className="text-lg font-bold text-white">{result.imported?.toLocaleString()} new domains imported</p>
              <p className="text-sm text-zinc-400">in {result.elapsed_seconds}s</p>
            </div>
            <div className="bg-zinc-800/50 rounded-sm p-3 text-left space-y-1.5">
              <p className="text-xs text-zinc-300 font-mono">Total in file: {result.total_in_file?.toLocaleString()}</p>
              <p className="text-xs text-emerald-400 font-mono">New imported: {result.imported?.toLocaleString()}</p>
              {(result.skipped_duplicates_in_csv > 0 || result.skipped_existing_in_db > 0) && (
                <p className="text-xs text-amber-400 font-mono">
                  Skipped: {((result.skipped_duplicates_in_csv || 0) + (result.skipped_existing_in_db || 0)).toLocaleString()} duplicates
                  {result.skipped_duplicates_in_csv > 0 && ` (${result.skipped_duplicates_in_csv.toLocaleString()} in CSV)`}
                  {result.skipped_existing_in_db > 0 && ` (${result.skipped_existing_in_db.toLocaleString()} already in DB)`}
                </p>
              )}
              <p className="text-xs text-zinc-500 font-mono">Total in database: {result.total_in_db?.toLocaleString()}</p>
              {result.import_group && (
                <p className="text-[10px] text-zinc-600 font-mono">Import group: {result.import_group}</p>
              )}
            </div>
            <Button
              data-testid="upload-close-btn"
              onClick={() => setOpen(false)}
              className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm"
            >
              Go to Dashboard
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
